import { z } from 'zod';
/**
 * Cộng tác trên task — spec mục 4 (`APM-504c`).
 */
/**
 * Cú pháp nhắc tên trong `body`: `@[Tên hiển thị](uuid)`.
 *
 * Lưu markup thay vì lưu tên trần: đổi tên hiển thị thì bình luận cũ vẫn trỏ
 * đúng người, và frontend render được thành link mà không phải đoán.
 */
export const MENTION_PATTERN = /@\[([^\]]{1,200})\]\(([0-9a-fA-F-]{36})\)/g;
/** Rút danh sách userId được nhắc trong một đoạn văn bản, đã khử trùng lặp. */
export function extractMentions(body) {
    const ids = new Set();
    for (const match of body.matchAll(MENTION_PATTERN)) {
        if (match[2])
            ids.add(match[2].toLowerCase());
    }
    return [...ids];
}
export const CommentBody = z
    .string()
    .trim()
    .min(1, 'Bình luận không được để trống')
    .max(10_000, 'Bình luận tối đa 10.000 ký tự');
export const CreateCommentInput = z.object({ body: CommentBody });
export const UpdateCommentInput = z.object({ body: CommentBody });
export const CommentView = z.object({
    id: z.uuid(),
    taskId: z.uuid(),
    authorId: z.uuid(),
    authorName: z.string().nullable(),
    body: z.string(),
    /** userId của những người được nhắc — frontend không phải parse lại. */
    mentions: z.array(z.uuid()),
    createdAt: z.iso.datetime(),
    /** `null` khi chưa từng sửa. Dùng để hiện nhãn "đã chỉnh sửa". */
    editedAt: z.iso.datetime().nullable(),
    /**
     * Bình luận kéo về từ nguồn đồng bộ — APM-SYNC-f, docs/plan-project-sync.md §5.5.
     * `authorId` khi đó là người ghép được hoặc **người tạo kết nối**, nên giao diện
     * hiện `authorName` gốc + tên nguồn ở đây, không hiện như người tạo kết nối viết.
     * `authorName` `null` khi bên kia không cho biết ai viết. Vắng / `null` = viết
     * trong ChillPM (kể cả bình luận đã đẩy ra ngoài).
     */
    external: z
        .object({ source: z.string(), authorName: z.string().nullable() })
        .nullable()
        .optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Một mục trong lịch sử thay đổi của task.
 *
 * Đọc thẳng từ `event_log`, **không** có bảng thứ hai. Event đã ghi đủ ai /
 * lúc nào / cũ→mới rồi; dựng bảng activity song song là tạo một nguồn sự thật
 * lệch được với nguồn mà Replay dùng.
 */
export const ActivityEntry = z.object({
    id: z.string(),
    eventType: z.string(),
    actorType: z.enum(['user', 'ai', 'system']),
    actorId: z.uuid().nullable(),
    actorName: z.string().nullable(),
    /** `{ before, after }` — chỉ những field thực sự đổi. */
    payload: z.record(z.string(), z.unknown()),
    /** Gom các event của cùng một thao tác, vd áp một CR đổi 40 task. */
    correlationId: z.string().nullable(),
    occurredAt: z.iso.datetime(),
    /**
     * Tên nguồn đồng bộ (vd "AI Service") khi thay đổi đến từ một lượt kéo về —
     * APM-SYNC-e, docs/plan-project-sync.md §5.6. Nhận ra qua `correlationId` =
     * id lượt chạy (`project_sync_run`), không qua `actorType`: lượt kéo chạy dưới
     * `system` vì API ngoài không cho biết ai đổi. Vắng / `null` = không phải đồng bộ.
     */
    syncSource: z.string().nullable().optional(),
});
export const ActivityFeed = z.object({
    entries: z.array(ActivityEntry),
    hasMore: z.boolean(),
});
/**
 * Một dòng lịch sử của **cả dự án** — APM-CHANGE-a.
 *
 * Khác `ActivityEntry` ở chỗ nó phải nói **thứ gì** vừa đổi: dòng thời gian của
 * một task thì mọi dòng đều về task ấy, còn dòng thời gian của cả dự án trộn
 * hai trăm task với sprint, tài liệu, RAID. Không có tên thì mỗi dòng là "ai đó
 * đổi ngày một cái gì đó".
 */
export const ProjectActivityEntry = ActivityEntry.extend({
    /** `task`, `sprint`, `document`… — loại aggregate đã bị đổi. */
    aggregateType: z.string(),
    aggregateId: z.uuid(),
    /**
     * Mã người dùng nhìn thấy (`HRM-17`), khi có.
     *
     * `null` cho những aggregate không mang mã — sprint, tài liệu, RAID. Không
     * bịa ra mã cho chúng: một mã giả trên màn hình là một mã người dùng sẽ đi
     * tìm ở chỗ khác và không thấy.
     */
    entityKey: z.string().nullable(),
    entityTitle: z.string().nullable(),
    /**
     * Dòng xoá của **lần xoá hiện tại** mà người xem lấy lại được. Vắng = không có nút.
     * - `task.deleted` (APM-TDEL-b): việc còn đang bị xoá, người xem là PM hoặc người tạo việc.
     * - `document.deleted` (APM-PDOC-a): tài liệu còn đang bị xoá, người xem có
     *   `document:approve` ở dự án — cùng cửa với lệnh xoá.
     */
    canRestore: z.boolean().optional(),
});
export const ProjectActivityFeed = z.object({
    entries: z.array(ProjectActivityEntry),
    /**
     * Con trỏ trang sau — `null` là hết.
     *
     * Cặp `(occurredAt, id)` chứ không riêng `id`: event ghi **muộn** với
     * `occurred_at` **cũ** (seed, import, backfill) có id nhỏ hơn mốc nhưng thời
     * gian sau mốc, nên lọc bằng một mình `id` sẽ đánh rơi chúng — cùng cạm bẫy
     * đã ghi ở docs/data-model.md §4 cho delta của Replay.
     */
    nextCursor: z.string().nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
/** File đính kèm của task. Tải về vẫn đi qua presigned URL của `APM-202`. */
export const AttachmentView = z.object({
    id: z.uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedBy: z.uuid(),
    uploadedByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** Người có thể được nhắc — dùng cho gợi ý khi gõ `@`. */
export const MentionCandidate = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    /**
     * Phòng ban — APM-1315. `null` khi chưa gán.
     *
     * Ô nhắc `@` là chỗ hai người trùng tên gây hại nhất: nhắc nhầm nghĩa là gửi
     * thông báo cho người không liên quan, và người cần biết thì không biết.
     */
    departmentName: z.string().nullable(),
});
//# sourceMappingURL=collaboration.js.map